import moment from 'moment';

import {
  MILITARY_TIME,
  DATE_TIME_FORMAT
} from 'bento-ordering/base/constants/timeFormat';

import {
  getLocationMenuCloseDateTimeSelector,
  getMenuIdForFulfillmentOptions
} from 'bento-ordering/menus/selectors';
import isLocationMenuExpired from 'bento-ordering/menus/utils/isLocationMenuExpired';
import isFulfillmentTimeOutOfRange from 'bento-ordering/menus/utils/isFulfillmentTimeOutOfRange';
import {
  MENU,
  MENU_ITEMS_DETAIL
} from 'bento-ordering/menus/constants/entities';

import { openNotificationModal } from 'bento-ordering/base/actions/notificationModals';
import { navigateToMenuItem } from 'bento-ordering/menus/actions/menuItem';
import {
  BETWEEN_HOURS,
  MENU_HOURS_EXPIRED
} from 'bento-ordering/cart/constants/notificationTypes';
import {
  getMenuEndpoint,
  getMenuDetailEndpoint
} from 'bento-ordering/base/constants/endpoints';
import {
  FETCH_REQUEST,
  FETCH_SUCCESS,
  FETCH_FAILURE,
  FETCH_LIST_REQUEST,
  FETCH_LIST_SUCCESS,
  FETCH_LIST_FAILURE
} from 'bento-ordering/base/actions/actionTypes';
import { apiAction } from 'bento-ordering/base/utils/apiHelpers';
import { getListTypes } from 'bento-ordering/base/actions/api';
import { setupInitialFlow } from 'bento-ordering/base/actions/setupInitialFlow';

const getActionTypesForEntity = (actionTypes, entity) => {
  return actionTypes.map(type => ({
    type: `[${entity}] ${type}`,
    meta: { entity }
  }));
};

export const fetchCurrentMenu = () => async (dispatch, getState) => {
  const {
    restaurantLocations,
    cart,
    menu,
    locationThrottledTimes
  } = getState();

  const menuId = getMenuIdForFulfillmentOptions(
    restaurantLocations,
    cart,
    locationThrottledTimes
  );
  // if there is no menu for fulFillment options
  // the cart is in a bad state and needs to be reset

  if (!menuId) {
    await dispatch(setupInitialFlow({ resetCart: true }));
    return null;
  }
  // we don't want to refetch a menu that we already have in
  // memory
  //
  if (menuId === menu.id) {
    return null;
  }
  const endpoint = getMenuEndpoint(menuId);

  const actionTypes = getActionTypesForEntity(
    [FETCH_REQUEST, FETCH_SUCCESS, FETCH_FAILURE],
    MENU
  );

  dispatch(apiAction(endpoint, 'GET', actionTypes));
};

export const fetchCurrentMenuDetails = () => (dispatch, getState) => {
  const {
    restaurantLocations,
    cart,
    menuItemsDetail,
    locationThrottledTimes
  } = getState();

  const menuId = getMenuIdForFulfillmentOptions(
    restaurantLocations,
    cart,
    locationThrottledTimes
  );
  if (menuItemsDetail.menuId === menuId || !menuId) {
    return null;
  }
  const endpoint = getMenuDetailEndpoint(menuId);
  const entity = MENU_ITEMS_DETAIL;
  const baseActionTypes = [
    FETCH_LIST_REQUEST,
    FETCH_LIST_SUCCESS,
    FETCH_LIST_FAILURE
  ];
  const metaData = { menuId };
  const queryParams = null;
  const actionTypes = getListTypes(
    entity,
    baseActionTypes,
    queryParams,
    metaData
  );

  dispatch(apiAction(endpoint, 'GET', actionTypes));
};

export const checkIfFulfillmentTimeIsInvalid = cart => {
  const fulfillmentIsInvalid = cart.requested_fulfillment_is_asap
    ? false
    : isFulfillmentTimeOutOfRange(
        cart.online_ordering_prepared_by_time,
        cart.current_prep_time
      );

  return fulfillmentIsInvalid;
};

export const checkIfCurrentMenuIsExpired = () => (dispatch, getState) => {
  const state = getState();
  const { cart } = state;
  const expiredDateTime = getLocationMenuCloseDateTimeSelector(state);

  const processedFulfillmentTime = cart.requested_fulfillment_is_asap
    ? moment().format()
    : moment(cart.fulfillment_time, MILITARY_TIME).format(DATE_TIME_FORMAT);

  const isExpired = isLocationMenuExpired(
    expiredDateTime,
    processedFulfillmentTime
  );

  return isExpired;
};

export const handleMenuItemClick = slug => (dispatch, getState) => {
  const state = getState();
  const { cart } = state;
  const fulfillmentIsInvalid = checkIfFulfillmentTimeIsInvalid(cart);
  const isExpired = dispatch(checkIfCurrentMenuIsExpired());

  const menuOrFulfillmentTimeHasExpired = fulfillmentIsInvalid || isExpired;

  if (menuOrFulfillmentTimeHasExpired && cart.items.length > 0) {
    dispatch(openNotificationModal(MENU_HOURS_EXPIRED));
  } else if (menuOrFulfillmentTimeHasExpired) {
    dispatch(openNotificationModal(BETWEEN_HOURS));
  } else if (!menuOrFulfillmentTimeHasExpired && !!slug) {
    dispatch(navigateToMenuItem(slug));
  }
  return menuOrFulfillmentTimeHasExpired;
};
